import { Control, useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { BadgeSelect, Loading } from '@/components/ui/daisy';
import { ORDENABLE_PAGE_SIZE, REQUEST_STALE_TIME_IN_MS } from '@/constants';
import useInfiniteService from '@/data/hook/useInfiniteService';
import { lessonBlocksQueryKeys } from '@/data/services/querykeys';
import ConditionalRenderer from '../ui/custom/ConditionalRenderer';
import Fieldset from '../ui/daisy/fieldset';
import { AssessmentFormFields } from './AssessmentForm';

type LessonBlockSelectFieldProps = {
  index: number;
  coursePathSlug: string;
  coursePathLabel: string;
  control: Control<AssessmentFormFields>;
};

export default function LessonBlockSelectField({
  index,
  coursePathSlug,
  coursePathLabel,
  control,
}: LessonBlockSelectFieldProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.evaluationsTab',
  });

  const { results: lessonBlocks, isLoading } = useInfiniteService({
    ...lessonBlocksQueryKeys.list(coursePathSlug, {
      pageSize: ORDENABLE_PAGE_SIZE,
    })._ctx.infinity,
    enabled: !!coursePathSlug,
    staleTime: REQUEST_STALE_TIME_IN_MS,
  });

  const { field } = useController({
    control,
    name: `lessonBlocksBySlave.${index}.lessonBlockIds` as const,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-3">
        <Loading variant="spinner" size="xs" color="primary" />
      </div>
    );
  }

  return (
    <BadgeSelect
      multiple
      label={coursePathLabel}
      placeholder={t('selectUnitsPlaceholder')}
      value={field.value.map(String)}
      onChange={values => field.onChange(values.map(Number))}
      onClear={() => field.onChange([])}
      clearAriaLabel={t('clearSelectedUnits')}
    >
      <ConditionalRenderer condition={!lessonBlocks.length}>
        <div className="p-2 flex justify-center">
          <Fieldset.Label>{t('noUnitsAvailable')}</Fieldset.Label>
        </div>
      </ConditionalRenderer>
      {lessonBlocks.map(lessonBlock => (
        <BadgeSelect.Option
          key={lessonBlock.id}
          value={String(lessonBlock.id)}
          color="info"
        >
          {lessonBlock.name}
        </BadgeSelect.Option>
      ))}
    </BadgeSelect>
  );
}
